import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { importAPI } from '../lib/api';
import { 
  Upload, 
  FileSpreadsheet, 
  Users, 
  BarChart3,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const BulkImport = () => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('designers');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [template, setTemplate] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const response = activeTab === 'designers' 
        ? await importAPI.importDesigners(file)
        : await importAPI.importScores(file);
      
      setResult(response.data);
      toast.success(`Import completed! ${response.data.inserted} inserted, ${response.data.updated} updated`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Import failed';
      toast.error(errorMsg);
      setResult({ error: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const loadTemplate = async (type) => {
    try {
      const response = await importAPI.getTemplate(type);
      setTemplate(response.data);
    } catch (error) {
      toast.error('Failed to load template info');
    }
  };

  const downloadSampleCSV = () => {
    if (!template) return;
    
    const headers = template.columns.join(',');
    const sampleValues = template.columns.map(col => {
      const val = template.sample_row[col];
      if (typeof val === 'string') return `"${val}"`;
      return val;
    }).join(',');
    
    const csvContent = `${headers}\n${sampleValues}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="bulk-import-page">
      {/* Header */}
      <div>
        <h1 className={cn(
          "text-2xl md:text-3xl font-bold font-outfit flex items-center gap-3",
          isDark ? "text-white" : "text-slate-800"
        )}>
          <Upload className="w-8 h-8 text-[#331062]" />
          Bulk Import
        </h1>
        <p className={cn("mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
          Import employee data and performance scores from Excel files
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === 'designers' ? 'default' : 'outline'}
          onClick={() => { setActiveTab('designers'); setFile(null); setResult(null); loadTemplate('designers'); }}
          className={activeTab === 'designers' ? 'bg-[#331062]' : ''}
          data-testid="tab-designers"
        >
          <Users className="w-4 h-4 mr-2" />
          Designers
        </Button>
        <Button
          variant={activeTab === 'scores' ? 'default' : 'outline'}
          onClick={() => { setActiveTab('scores'); setFile(null); setResult(null); loadTemplate('scores'); }}
          className={activeTab === 'scores' ? 'bg-[#331062]' : ''}
          data-testid="tab-scores"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Performance Scores
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Card */}
        <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
          <CardHeader>
            <CardTitle className={cn("text-lg flex items-center gap-2", isDark ? "text-white" : "")}>
              <FileSpreadsheet className="w-5 h-5" />
              Upload {activeTab === 'designers' ? 'Designers' : 'Scores'} Excel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File Input */}
            <div 
              className={cn(
                "border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors",
                isDark ? "border-slate-600 hover:border-slate-500" : "border-slate-300 hover:border-slate-400",
                file && "border-green-500 bg-green-50 dark:bg-green-900/20"
              )}
              onClick={() => document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="space-y-2">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
                  <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                    {file.name}
                  </p>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className={cn("w-12 h-12 mx-auto", isDark ? "text-slate-500" : "text-slate-400")} />
                  <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                    Click to select Excel file
                  </p>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                    Supports .xlsx and .xls formats
                  </p>
                </div>
              )}
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full bg-[#331062] hover:bg-[#4a1d8a]"
              data-testid="upload-btn"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import {activeTab === 'designers' ? 'Designers' : 'Scores'}
                </>
              )}
            </Button>

            {/* Result */}
            {result && (
              <div className={cn(
                "p-4 rounded-lg",
                result.error 
                  ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800" 
                  : "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
              )}>
                {result.error ? (
                  <div className="flex items-start gap-2">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-700 dark:text-red-400">Import Failed</p>
                      <p className="text-sm text-red-600 dark:text-red-300">{result.error}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <p className="font-medium text-green-700 dark:text-green-400">Import Successful</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className={cn("p-2 rounded", isDark ? "bg-slate-700" : "bg-white")}>
                        <p className={isDark ? "text-slate-400" : "text-slate-500"}>Inserted</p>
                        <p className={cn("font-bold text-lg", isDark ? "text-white" : "text-slate-800")}>{result.inserted}</p>
                      </div>
                      <div className={cn("p-2 rounded", isDark ? "bg-slate-700" : "bg-white")}>
                        <p className={isDark ? "text-slate-400" : "text-slate-500"}>Updated</p>
                        <p className={cn("font-bold text-lg", isDark ? "text-white" : "text-slate-800")}>{result.updated}</p>
                      </div>
                      {result.skipped !== undefined && (
                        <div className={cn("p-2 rounded", isDark ? "bg-slate-700" : "bg-white")}>
                          <p className={isDark ? "text-slate-400" : "text-slate-500"}>Skipped</p>
                          <p className={cn("font-bold text-lg", isDark ? "text-white" : "text-slate-800")}>{result.skipped}</p>
                        </div>
                      )}
                    </div>
                    {result.errors && result.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          Errors ({result.errors.length}):
                        </p>
                        <ul className="text-xs text-amber-600 dark:text-amber-300 mt-1 space-y-1">
                          {result.errors.map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Template Info Card */}
        <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
          <CardHeader>
            <CardTitle className={cn("text-lg flex items-center gap-2", isDark ? "text-white" : "")}>
              <Info className="w-5 h-5" />
              Template Format
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeTab === 'designers' ? (
              <>
                <div>
                  <p className={cn("text-sm font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
                    Required Columns:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Name', 'Email ID'].map(col => (
                      <span key={col} className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded font-mono">
                        {col}*
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className={cn("text-sm font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
                    Optional Columns:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Employee ID', 'DOJ', 'Department Name', 'Designation Name', 'Direct manager', 'static/motion', 'location'].map(col => (
                      <span key={col} className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded font-mono">
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={cn("p-3 rounded-lg text-sm", isDark ? "bg-slate-700" : "bg-slate-50")}>
                  <p className={cn("font-medium mb-1", isDark ? "text-white" : "text-slate-800")}>Column Mapping:</p>
                  <ul className={cn("text-xs space-y-1", isDark ? "text-slate-300" : "text-slate-600")}>
                    <li>• <strong>Designation Name</strong> → Role</li>
                    <li>• <strong>Department Name</strong> → Vertical Team</li>
                    <li>• <strong>DOJ</strong> → Date of Joining</li>
                  </ul>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className={cn("text-sm font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
                    Required Columns:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['email', 'year', 'month', 'week', 'quality_consistency', 'brief_understanding', 'speed_vs_accuracy', 'guideline_adherence', 'team_discipline'].map(col => (
                      <span key={col} className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded font-mono">
                        {col}*
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className={cn("text-sm font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
                    Optional Columns:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['escalation', 'positive_feedback', 'unplanned_leave', 'comments'].map(col => (
                      <span key={col} className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded font-mono">
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={cn("p-3 rounded-lg text-sm space-y-1", isDark ? "bg-slate-700" : "bg-slate-50")}>
                  <p className={isDark ? "text-slate-300" : "text-slate-600"}>
                    • Scores: 1-5 (integer)
                  </p>
                  <p className={isDark ? "text-slate-300" : "text-slate-600"}>
                    • Escalation: "None" or "Major"
                  </p>
                  <p className={isDark ? "text-slate-300" : "text-slate-600"}>
                    • Feedback/Leave: TRUE or FALSE
                  </p>
                </div>
              </>
            )}

            <Button
              variant="outline"
              onClick={downloadSampleCSV}
              className="w-full"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Sample CSV
            </Button>

            <div className={cn(
              "p-3 rounded-lg border-l-4 border-l-blue-500",
              isDark ? "bg-blue-900/20" : "bg-blue-50"
            )}>
              <p className={cn("text-sm", isDark ? "text-blue-300" : "text-blue-700")}>
                <strong>Tip:</strong> Existing records will be updated based on email (designers) or email + year + month + week (scores).
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BulkImport;
